"use client";
import CompletePaymentModal from "@/components/microsite/CompletePaymentModal";
import { useHash } from "@/hooks/useHash";
import { handleApiErr, useAPI } from "@/lib/api";
import { cn } from "@/lib/utils";
import assetUrl from "@/vars/assetUrl";
import cdnUrl from "@/vars/cdnUrl";
import { Form, Formik, useFormikContext } from "formik";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronUpIcon,
  CreditCardIcon,
  MinusCircle,
  PlusCircle,
  SearchIcon,
  ShoppingCartIcon,
  SquarePenIcon,
  TicketIcon,
  Trash2Icon,
  TrashIcon,
  XCircleIcon,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import * as Yup from "yup";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import Button, { FormikSubmit } from "../Button";
import Card from "../Card";
import ErrorField from "../ErrorField";
import { FieldInput, FieldTextarea, InputLabel } from "../Input";
import ModalComponent, { ConfirmModal, ModalHeader, useModal } from "../Modal";
import { Skeleton } from "../ui/skeleton";

type CartFormType = {
  full_name: string;
  email: string;
  phone: string;
  payment_method: string;
  voucher_code?: string;
};

export const useCartStoreState = create<{
  showCart: number;
  isLoading: boolean;
  subTotal: number;
  discount: number;
  discountPercentage: number;
  discountProductName: string;
  serviceFee: number;
  paymentFee: number;
  grandTotal: number;
  setShowCart(): void;
  setLoading(state: boolean): void;
  setSummary({
    subTotal,
    discount,
    discountPercentage,
    discountProductName,
    serviceFee,
    paymentFee,
    grandTotal,
  }): void;
}>((set, get) => ({
  showCart: 0,
  isLoading: false,
  subTotal: 0,
  discount: 0,
  discountPercentage: 0,
  discountProductName: "",
  serviceFee: 0,
  paymentFee: 0,
  grandTotal: 0,
  setShowCart: () => set({ showCart: get().showCart + 1 }),
  setLoading: (state) => set({ isLoading: state }),
  setSummary({
    subTotal,
    discount,
    discountPercentage,
    discountProductName,
    serviceFee,
    paymentFee,
    grandTotal,
  }) {
    set({
      subTotal,
      discount,
      discountPercentage,
      discountProductName,
      serviceFee,
      paymentFee,
      grandTotal,
    });
  },
}));

export const useCartStore = create<{
  carts: any[];
  add(item: any): void;
  update(item: any, index: number): void;
  remove(index: number): void;
  reset(): void;
}>()(
  persist(
    (set, get) => ({
      carts: [] as any[],
      add: (item) => set({ carts: [...get().carts, item] }),
      update: (item, index) => {
        set({
          carts: get().carts.map((cart, i) => (i === index ? item : cart)),
        });
      },
      remove: (index) => {
        set({ carts: get().carts.filter((_, i) => i !== index) });
      },
      reset: () => set({ carts: [] }),
    }),
    {
      name: "_sid_cart",
    }
  )
);

export default function MicrositeCart({ data }) {
  const paymentModal = useModal();
  const cartModal = useModal();
  const carts = useCartStore((s) => s.carts);
  const showCartCount = useCartStoreState((s) => s.showCart);
  const prevCount = useRef(0);
  const hash = useHash();
  const { push } = useRouter();
  const prevHash = useRef("");

  useEffect(() => {
    if (hash?.startsWith("#payment=")) {
      paymentModal.open();
    } else if (
      prevHash.current.startsWith("#payment=") &&
      !hash.startsWith("#payment=")
    ) {
      paymentModal.close();
    }
    prevHash.current = hash;
  }, [hash]);

  useEffect(() => {
    if (prevCount.current !== showCartCount) {
      cartModal.open();
    }
    prevCount.current = showCartCount;
  }, [showCartCount]);

  const closePaymentModal = () => {
    push(window.location.pathname);
  };

  return (
    <Fragment>
      <div
        className={cn(
          "right-0 bottom-0 left-0 z-50 fixed flex justify-center pb-10 w-full transition-transform pointer-events-none",
          carts.length > 0 ? "" : "translate-y-full"
        )}
      >
        <button
          className="relative flex items-center bg-black px-6 py-3 border border-white rounded-full text-white hover:scale-105 pointer-events-auto"
          onClick={cartModal.open}
        >
          <ShoppingCartIcon className="mr-2 w-4" />{" "}
          <strong>{carts.length} Items</strong>{" "}
          <ChevronUpIcon className="ml-2 size-4" />
          <div className="top-1 -right-0 absolute bg-red-500 rounded-full ring-4 ring-red-500/50 size-3 animate-pulse" />
        </button>
      </div>
      <ModalComponent {...cartModal} disabled drawer className="md:!max-w-lg">
        <ModalHeader {...cartModal} className="flex items-center">
          <ShoppingCartIcon className="mr-2 size-5" /> Carts
        </ModalHeader>
        <CartModalContent onClose={cartModal.close} micrositeData={data} />
      </ModalComponent>

      <ModalComponent
        {...paymentModal}
        disabled
        drawer
        className="relative md:!max-w-lg"
      >
        <CompletePaymentModal
          modalProp={paymentModal}
          hash={hash}
          closePaymentModal={closePaymentModal}
        />
      </ModalComponent>
    </Fragment>
  );
}

function CartModalContent({ onClose, micrositeData }) {
  const api = useAPI();
  const carts = useCartStore((s) => s.carts);
  const [step, setStep] = useState<"CART" | "PAYMENT">("CART");
  const resetCart = useCartStore((s) => s.reset);
  const [globalErr, setGlobalErr] = useState("");

  const grandTotal = useCartStoreState((s) => s.grandTotal);

  const confirmState = useModal();

  useEffect(() => {
    if (carts.length === 0) {
      onClose();
    }
  }, [carts]);

  const validationSchema = Yup.object().shape({
    full_name: Yup.string()
      .required("Full Name is required")
      .min(3, "Full name must be at least 3 characters")
      .max(100, "Full name must be less than 100 characters"),
    email: Yup.string()
      .email("Invalid email")
      .required("Email is required")
      .min(3, "Email must be at least 3 characters")
      .max(100, "Email must be less than 100 characters"),
    phone: Yup.string()
      .test({
        test: (v) => {
          if (!v) return true;
          if (/^[0-9]+$/.test(v)) {
            return true;
          }
          return false;
        },
        message: "Phone number must be numeric",
      })
      .max(13, "Phone number must be 13 digits"),
    payment_method: Yup.string().required("Payment method is required"),
  });

  const onSubmitCart = async (values) => {
    setGlobalErr("");
    if (grandTotal !== 0) {
      if (grandTotal < 10000) {
        setGlobalErr("Minimum order amount is IDR 10.000");
        return;
      }
    }
    try {
      const { data } = await api.post("/microsite/shop/checkout", {
        items: carts,
        payment_method: values.payment_method,
        full_name: values.full_name,
        email: values.email,
        phone: values.phone,
        microsite_id: micrositeData?._id || null,
        voucher_code: values.voucher_code,
      });

      //delayed response
      await new Promise((r) => setTimeout(r, 1000));

      //@ts-ignore
      if (typeof window.fbq !== "undefined") {
        //@ts-ignore
        window.fbq("track", "InitiateCheckout", {
          value: grandTotal,
          currency: "IDR",
        });
      }

      //@ts-ignore
      window.location =
        window.location.pathname + `#payment=${data.id}.${data.hash}`;
      onClose();
      resetCart();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Formik
      initialValues={
        {
          full_name: "",
          email: "",
          payment_method: "",
          voucher_code: "",
        } as CartFormType
      }
      validationSchema={validationSchema}
      onSubmit={onSubmitCart}
    >
      {({ submitForm, isSubmitting }) => (
        <Fragment>
          {step === "CART" && (
            <div className="flex flex-col gap-3 mt-6 mb-6">
              {carts.map((item, index) => (
                <CartItem
                  id={item.id}
                  qty={item.qty}
                  index={index}
                  key={item.id}
                />
              ))}
            </div>
          )}

          {step == "PAYMENT" && (
            <div className="flex flex-col gap-3 mt-6 mb-6">
              <PaymentMethods />
              <ErrorField name="payment_method" className="!-mt-3" />
              <VoucherComponent />
            </div>
          )}

          <CartSummary />

          {step === "CART" && (
            <div className="right-0 bottom-0 left-0 sticky bg-white pb-3">
              <div className="flex gap-2 mb-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={confirmState.open}
                >
                  <Trash2Icon className="size-4" /> Clear Cart
                </Button>
                <Button
                  variant="blue"
                  className="w-full font-semibold"
                  onClick={() => {
                    setStep("PAYMENT");
                  }}
                >
                  Continue
                  <ArrowRightIcon />
                </Button>
              </div>

              <Button variant="outline" onClick={onClose} className="w-full">
                <ShoppingCartIcon />
                Continue Shopping
              </Button>
            </div>
          )}

          {step === "PAYMENT" && (
            <Fragment>
              {globalErr && (
                <div className="bg-red-50 mb-3 p-3 border border-red-500 rounded-md text-red-500 text-sm">
                  {globalErr}
                </div>
              )}
              <div className="right-0 bottom-0 left-0 sticky flex gap-2 bg-white pb-3">
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={isSubmitting}
                  onClick={() => setStep("CART")}
                >
                  <ArrowLeftIcon className="size-4" />
                  Back
                </Button>
                <FormikSubmit
                  variant="blue"
                  className="w-full font-semibold"
                  onClick={submitForm}
                >
                  <CreditCardIcon className="size-4" />
                  Make a Payment
                </FormikSubmit>
              </div>
            </Fragment>
          )}

          <ConfirmModal
            modalProp={confirmState}
            title="Confirmation"
            content="Are you sure want to clear all cart item?"
            onOk={async () => {
              resetCart();
              onClose();
              return true;
            }}
          />
        </Fragment>
      )}
    </Formik>
  );
}

const CartSummary = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<CartFormType>();
  const api = useAPI();
  const carts = useCartStore((s) => s.carts);
  const setSummary = useCartStoreState((s) => s.setSummary);
  const setLoading = useCartStoreState((s) => s.setLoading);
  const isLoading = useCartStoreState((s) => s.isLoading);
  const summary = useCartStoreState(
    useShallow((s) => ({
      subTotal: s.subTotal,
      discount: s.discount,
      discountPercentage: s.discountPercentage,
      discountProductName: s.discountProductName,
      serviceFee: s.serviceFee,
      paymentFee: s.paymentFee,
      grandTotal: s.grandTotal,
    }))
  );

  useEffect(() => {
    const estimateCart = async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/microsite/shop/checkout/estimate", {
          items: carts,
          payment_method: values.payment_method,
          voucher_code: values.voucher_code,
        });

        let discountPercentage = 0;
        let discountProductName = "";

        for (const inv of data.invoices) {
          for (const voucher of inv.vouchers || []) {
            discountPercentage = voucher.discount;
            discountProductName = voucher.product_name;
          }
        }

        setSummary({
          subTotal: data.invoices.reduce((a, b) => a + b.sub_total, 0),
          discount: data.invoices.reduce((a, b) => a + b.discount, 0) || 0,
          discountPercentage,
          discountProductName,
          serviceFee: data.service_fee || 0,
          paymentFee: data.payment_fee || 0,
          grandTotal: data.grand_total,
        });
      } catch (e) {
        handleApiErr(e, { t, toast });
      } finally {
        setLoading(false);
      }
    };
    if (carts.length > 0) {
      estimateCart();
    }
    return () => {};
  }, [carts, values.payment_method, values.voucher_code]);

  return (
    <Fragment>
      <h3 className="mb-2 font-bold text-foreground/80">Summary</h3>
      <table className="mb-4 w-full text-sm">
        <tbody>
          <tr>
            <td className="py-1 pb-1 font-semibold text-foreground/50">
              Sub Total
            </td>
            <td className="py-1 pb-1 w-[1%] text-right truncate">
              {isLoading ? (
                <Skeleton className="w-24 h-4" />
              ) : (
                <>Rp {Intl.NumberFormat("id").format(summary.subTotal)}</>
              )}
            </td>
          </tr>
          <tr>
            <td className="py-1 pb-1 font-semibold text-foreground/50">
              Discount
            </td>
            <td className="py-1 pb-1 w-[1%] text-right truncate">
              {isLoading ? (
                <Skeleton className="w-24 h-4" />
              ) : (
                <>Rp {Intl.NumberFormat("id").format(summary.discount)}</>
              )}
            </td>
          </tr>
          <tr>
            <td className="py-1 pb-1 font-semibold text-foreground/50">
              Payment Fee
            </td>
            <td className="py-1 pb-1 w-[1%] text-right truncate">
              {isLoading ? (
                <Skeleton className="w-24 h-4" />
              ) : (
                <>Rp {Intl.NumberFormat("id").format(summary.paymentFee)}</>
              )}
            </td>
          </tr>
          <tr>
            <td className="py-1 border-t font-semibold text-foreground text-base">
              Grand Total
            </td>
            <td className="py-1 border-t w-[1%] font-bold text-base text-right truncate">
              {isLoading ? (
                <Skeleton className="w-24 h-4" />
              ) : (
                <>Rp {Intl.NumberFormat("id").format(summary.grandTotal)}</>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </Fragment>
  );
};

const CartItem = ({ id, index, qty }) => {
  const carts = useCartStore((s) => s.carts);
  const updateCart = useCartStore((s) => s.update);
  const removeCart = useCartStore((s) => s.remove);
  const delModal = useModal();
  const notesModal = useModal();
  const { data, isLoading } = useSWR(
    id ? "/microsite/shop/items/" + id : null,
    {
      revalidateOnFocus: true,
      shouldRetryOnError: false,
    }
  );

  if (isLoading) {
    return <div></div>;
  }

  if (data?.data) {
    return (
      <div className="flex p-2 border rounded-md">
        <div
          className="bg-slate-200 bg-cover bg-center rounded-md size-16 shrink-0"
          style={{
            backgroundImage: `url(${assetUrl + data?.data?.images[0]?.image})`,
          }}
        ></div>
        <div className="flex-1 pl-2">
          <p>{data?.data?.name}</p>

          {data?.data?.is_discount ? (
            <>
              <p className="text-foreground/60 text-sm">
                Rp{" "}
                {Intl.NumberFormat("id").format(
                  data?.data?.discount_amount?.idr
                )}
              </p>
              <p className="text-foreground/30 text-xs line-through">
                Rp {Intl.NumberFormat("id").format(data?.data?.price?.idr)}
              </p>
            </>
          ) : (
            <>
              <p className="text-foreground/60 text-sm">
                Rp {Intl.NumberFormat("id").format(data?.data?.price?.idr)}
              </p>
            </>
          )}

          <p
            className={cn(
              "text-foreground/50 text-xs",
              !data.data.is_unlimited && data.data.stock <= 0
                ? "text-red-500"
                : ""
            )}
          >
            {data.data.is_unlimited
              ? "Unlimited"
              : `Stock: ${data.data.stock || 0}`}
          </p>
          <div className={cn("flex items-center mt-1 text-sm")}>
            {!(!data.data.is_unlimited && data.data.stock <= 0) && (
              <>
                <button
                  type="button"
                  className="text-foreground/50"
                  onClick={() => {
                    updateCart(
                      { ...carts[index], qty: qty <= 1 ? 1 : qty - 1 },
                      index
                    );
                  }}
                >
                  <MinusCircle className="size-4" />
                </button>
                <div className="mx-2 w-4 text-xs text-center">{qty}</div>
                <button
                  type="button"
                  className="mr-3 text-foreground/50"
                  onClick={() => {
                    if (!data.data.is_unlimited && qty + 1 > data.data.stock) {
                      return;
                    }
                    updateCart({ ...carts[index], qty: qty + 1 }, index);
                  }}
                >
                  <PlusCircle className="size-4" />
                </button>

                <button
                  type="button"
                  className="relative mr-3 text-foreground/50"
                  onClick={notesModal.open}
                >
                  <div
                    className={cn(
                      "-top-1 -right-1 absolute bg-green-500 rounded-full size-2.5",
                      carts[index].notes?.length > 0 ? "block" : "hidden"
                    )}
                  />
                  <SquarePenIcon className="size-4" />
                </button>
              </>
            )}

            <button
              type="button"
              className="text-foreground/50"
              onClick={delModal.open}
            >
              <TrashIcon className="size-4" />
            </button>
          </div>
        </div>
        <div className="font-medium text-sm text-right truncate">
          Rp{" "}
          {Intl.NumberFormat("id").format(
            qty *
              (data?.data?.is_discount
                ? data?.data?.discount_amount?.idr
                : data?.data?.price?.idr)
          )}
        </div>
        <ConfirmModal
          modalProp={delModal}
          title="Confirmation"
          content="Are you sure want to delete this item?"
          onOk={async () => {
            removeCart(index);
            return true;
          }}
        />
        <ModalComponent {...notesModal}>
          <ModalHeader {...notesModal}>Notes</ModalHeader>
          <CartNotesComponent
            value={carts[index].notes}
            onChange={(notes) => {
              notesModal.close();
              updateCart({ ...carts[index], notes }, index);
            }}
          />
        </ModalComponent>
      </div>
    );
  }

  return <div className="p-3 border">Item Not Found</div>;
};

const CartNotesComponent = ({ value, onChange }) => {
  const onChangeForm = (values) => {
    onChange(values.notes);
  };

  return (
    <Formik initialValues={{ notes: value }} onSubmit={onChangeForm}>
      <Form className="flex flex-col gap-3">
        <div>
          <FieldTextarea
            name="notes"
            placeholder="Notes"
            className="w-full"
            maxLength={100}
          />
          <p className="text-muted-foreground text-sm">
            Leave a note below, or keep empty if you don't have any notes
          </p>
        </div>
        <div className="flex justify-end">
          <FormikSubmit variant="blue">Save Notes</FormikSubmit>
        </div>
      </Form>
    </Formik>
  );
};

function PaymentMethods() {
  const { data } = useSWR("/subscriptions/available-payment");
  const { setFieldValue } = useFormikContext();
  const grandTotal = useCartStoreState((s) => s.grandTotal);
  const discountPercentage = useCartStoreState((s) => s.discountPercentage);

  const methods = useMemo(() => {
    if (data?.data) {
      const grouped = data.data.reduce((acc, item) => {
        acc[item.category] = acc[item.category] || [];
        acc[item.category].push(item);
        return acc;
      }, {});
      return grouped;
    }
    return {};
  }, [data]);

  useEffect(() => {
    if (grandTotal === 0) {
      setFieldValue("payment_method", "QRIS");
    }
  }, [discountPercentage, grandTotal]);

  return (
    <div>
      <div className="mb-4 pb-4 border-b text-sm">
        <div className="mb-3">
          <InputLabel required>Full Name</InputLabel>
          <FieldInput
            type="text"
            inputSize="lg"
            name="full_name"
            className="w-full text-base"
            placeholder="Full Name"
            autoFocus
            required
          />
          <ErrorField name="full_name" />
        </div>

        <div className="mb-3">
          <InputLabel required>Email</InputLabel>
          <FieldInput
            type="email"
            inputSize="lg"
            name="email"
            className="w-full text-base"
            placeholder="ex: nfikri@s.id"
            required
          />
          <ErrorField name="email" />
        </div>

        <div className="mb-3">
          <InputLabel>Phone (Optional)</InputLabel>
          <FieldInput
            type="text"
            inputSize="lg"
            name="phone"
            className="w-full text-base"
            placeholder="08 123 456 789"
          />
          <ErrorField name="phone" />
        </div>
      </div>

      <h3 className="mb-3 font-bold text-foreground/80">
        Select Payment Method
      </h3>

      {grandTotal === 0 ? (
        <div className="flex flex-col bg-green-50 p-3 border border-green-500 rounded-md text-green-600 text-sm text-center">
          <div className="text-2xl">🥳</div>
          <p className="font-bold">Free of charge</p>
          <p className="text-green-600/80">
            This transaction not require payment
          </p>
        </div>
      ) : (
        <Fragment>
          <RenderPaymentMethodGroup label="QRIS" items={methods["QRCODE"]} />
          <RenderPaymentMethodGroup
            label="Virtual Account"
            items={methods["VIRTUAL_ACCOUNT"]}
          />
          <RenderPaymentMethodGroup
            label="E-Wallet"
            items={methods["EWALLET"]}
          />
        </Fragment>
      )}
    </div>
  );
}

function RenderPaymentMethodGroup({ items, label }) {
  const { setFieldValue, values } = useFormikContext<CartFormType>();

  if (!items) {
    return <Fragment />;
  }
  return (
    <div className="mb-3">
      <div className="mb-1 font-bold text-foreground/80 text-sm">{label}</div>
      <div className="flex flex-wrap -m-1">
        {items.map((item) => (
          <div
            className={cn("p-1", items.length === 1 ? "w-full" : "w-4/12")}
            key={item.name}
          >
            <button
              className={cn(
                "p-2 border rounded-md w-full h-12 transition-all",
                item.name === values.payment_method
                  ? "border-black ring-1 ring-black bg-secondary"
                  : ""
              )}
              type="button"
              onClick={() => {
                setFieldValue("payment_method", item.name);
              }}
            >
              <img
                src={cdnUrl + "/images/payments/" + item.image}
                alt={item.name}
                className="w-full h-full contain-content"
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VoucherComponent() {
  const voucherModal = useModal();
  const { values, setFieldValue } = useFormikContext<any>();
  const code = values?.voucher_code;

  const isLoading = useCartStoreState((s) => s.isLoading);

  const summary = useCartStoreState(
    useShallow((s) => ({
      discountPercentage: s.discountPercentage,
      discountProductName: s.discountProductName,
    }))
  );

  return (
    <div className="pb-6 border-b">
      {code ? (
        <div>
          <h3 className="mb-1 font-bold text-sm">Use Voucher Code</h3>
          {isLoading ? (
            <Card className="p-3">
              <Skeleton className="w-full h-4" />
              <Skeleton className="mt-3 w-6/12 h-3" />
            </Card>
          ) : code && summary.discountPercentage === 0 ? (
            <Card className="flex border-red-500 overflow-hidden text-sm">
              <div className="flex justify-center items-center bg-red-500 px-3 text-white">
                <XCircleIcon className="size-5" />
              </div>
              <div className="flex-1 p-3 shrink-0">
                <p className="font-bold">Cannot use Voucher Code</p>
                <p>
                  Either voucher code is invalid, expired or cannot be used on
                  some item / tenant
                </p>
              </div>
              <div className="p-1 shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  className="justify-center items-center size-7 text-xs"
                  onClick={() => {
                    setFieldValue("voucher_code", "");
                  }}
                >
                  <Trash2Icon className="size-3" />
                </Button>
              </div>
            </Card>
          ) : (
            <Card className="flex border-green-600 overflow-hidden text-sm">
              <div className="flex justify-center items-center bg-green-600 px-3 py-1 font-bold text-white text-lg">
                {summary.discountPercentage}%
              </div>
              <div className="flex-1 p-2 shrink-0">
                <p className="font-bold">{code}</p>
                {summary.discountProductName && (
                  <p className="text-muted-foreground text-xs">
                    For product: {summary.discountProductName}
                  </p>
                )}
              </div>
              <div className="p-1 shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  className="justify-center items-center size-7 text-xs"
                  onClick={() => {
                    setFieldValue("voucher_code", "");
                  }}
                >
                  <Trash2Icon className="size-3" />
                </Button>
              </div>
            </Card>
          )}
        </div>
      ) : (
        <Button
          type="button"
          className="w-full"
          variant="secondary"
          onClick={voucherModal.open}
        >
          <TicketIcon className="size-4" /> Use Voucher Code
        </Button>
      )}
      <ModalComponent {...voucherModal} drawer>
        <ModalHeader {...voucherModal}>Use Voucher Code</ModalHeader>
        <VoucherForm
          onChange={(code) => {
            setFieldValue("voucher_code", code);
            voucherModal.close();
          }}
        />
      </ModalComponent>
    </div>
  );
}

function VoucherForm({ onChange }) {
  const { t } = useTranslation();
  const carts = useCartStore((s) => s.carts);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const api = useAPI();
  const onSubmit = async (values) => {
    setError(null);
    try {
      const { data } = await api.post("/microsite/shop/voucher/check", {
        code: values.code,
        items: carts,
      });
      setSuccess(data);
    } catch (e) {
      setSuccess(null);
      //...
      handleApiErr(e, {
        toast,
        t,
        onError: (message) => {
          switch (message) {
            case "entity_not_found":
              setError("voucher_not_found");
              return true;
          }
          return false;
        },
      });
    }
  };

  const validationSchema = Yup.object().shape({
    code: Yup.string().required("Voucher code is required"),
  });

  return (
    <Formik
      initialValues={{ code: "" }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      <Form className="pb-20 md:pb-0">
        <div>
          <div className="flex md:flex-row flex-col gap-3 md:gap-2">
            <FieldInput
              name="code"
              placeholder="Enter voucher code"
              inputSize="lg"
              className="w-full"
              autoFocus
            />
            <FormikSubmit variant="dark" className="flex items-center shrink-0">
              <SearchIcon className="mr-1 size-4" />
              Check
            </FormikSubmit>
          </div>
          <ErrorField name="code" />
        </div>
        <div className="mt-3">
          {error ? (
            <Card className="flex border-red-600 overflow-hidden text-sm">
              <div className="flex justify-center items-center bg-red-600 px-5 py-1 font-bold text-white text-lg">
                <XCircleIcon className="size-4" />
              </div>
              <div className="flex-1 p-2 shrink-0">
                <p className="font-bold">Voucher Not available</p>
                <p className="font-medium text-muted-foreground">
                  Your voucher is not available or has been expired
                </p>
              </div>
            </Card>
          ) : success ? (
            <Card className="flex border-green-600 overflow-hidden text-sm">
              <div className="flex justify-center items-center bg-green-600 px-3 py-1 font-bold text-white text-lg">
                {success.discount}%
              </div>
              <div className="flex-1 p-2 shrink-0">
                <p className="font-bold">Voucher Available!</p>

                <p className="font-medium text-muted-foreground">
                  {success.code}
                </p>
                {success.product_id && (
                  <p className="text-muted-foreground text-xs">
                    Discount for product: {success.product_name}
                  </p>
                )}
              </div>
              <div className="flex flex-col justify-center items-center px-3 shrink-0">
                <Button
                  variant="green"
                  size="sm"
                  type="button"
                  onClick={() => {
                    onChange(success.code);
                  }}
                >
                  Use Code
                </Button>
              </div>
            </Card>
          ) : (
            <Card className="flex flex-col justify-center items-center p-4 text-muted-foreground">
              <TicketIcon className="mb-2 size-5" />
              <p className="text-sm">
                Enter your voucher code to check availability
              </p>
            </Card>
          )}
        </div>
      </Form>
    </Formik>
  );
}
